import * as Sentry from '@sentry/react'

import { Routes, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router'

import { ExtraErrorData } from '@sentry/integrations'
import { ENVIRONMENT } from 'constants/application'
import { getRuntimeConfig } from 'constants/API/runtimeConfig'
import { isPreviewHost } from './isPreviewHost'
import { Auth0User } from 'models/auth'
import React from 'react'

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN
const BUILD_RELEASE = process.env.REACT_APP_SHORT_SHA

/** Header / field names whose values must never leave the client (case-insensitive). */
const SENSITIVE_KEYS = ['authorization', 'cookie', 'set-cookie', 'x-api-key', 'proxy-authorization']

/**
 * Field names whose string values are URLs. Their query string is stripped
 * wherever they appear (request.url, Referer/Origin headers, breadcrumb
 * data.url/to/from, tracing-span `http.url`, …) because the value — not the key
 * — carries the secret/PII (`?token=`, `?email=` in magic links), which key-name
 * redaction cannot catch.
 */
const URL_KEYS = ['url', 'to', 'from', 'referer', 'referrer', 'origin', 'http.url', 'http.query', 'fullurl', 'href', 'location', 'verifyurl']

/**
 * Strips the query string and fragment from a URL, keeping only the path/origin.
 * Query params can carry secrets/PII (e.g. `?token=`, `?email=` in magic links)
 * that must not reach Sentry. Kept local to avoid coupling Sentry init to the
 * API module's dependency graph.
 */
const stripUrlQuery = (url?: string): string | undefined => url?.split(/[?#]/)[0]

/**
 * Whether a string looks like a URL we should query-strip. Guards the generic
 * `URL_KEYS` (`to`/`from`/`origin`) so non-URL free text (date ranges, redux
 * transitions, `"Why? Because…"`) isn't truncated at its first `?`/`#`.
 * Case-insensitive: URL schemes are case-insensitive per RFC 3986.
 */
const isUrlLike = (value: string): boolean => /^(https?:)?\/\//i.test(value) || value.startsWith('/')

/** Matches a description that contains a URL — an http(s):// token or a path segment. */
const DESCRIPTION_HAS_URL = /(https?:\/\/|(?:^|\s)\/)/i

/**
 * Strips the query string from a tracing-span `description`. BrowserTracing
 * builds http-span descriptions as `${method} ${url}` (e.g. "GET https://api/x?token=…").
 * When the description contains a URL, everything from the first `?`/`#` is
 * dropped — robust even when the query holds an unencoded space (which would
 * otherwise split the URL into extra tokens and leak). Non-URL descriptions
 * (e.g. a custom span named "Is user eligible?") are left untouched so a stray
 * `?` in free text isn't truncated.
 */
const stripDescriptionUrl = (description: string): string =>
  DESCRIPTION_HAS_URL.test(description) ? description.split(/[?#]/)[0] : description

/**
 * Defense-in-depth scrubber: recursively redacts sensitive header/field values
 * anywhere in a Sentry event payload, so no code path can leak Auth0 bearer
 * tokens or credentials to Sentry (ISSUE-2094 backstop). Bounded in depth to
 * avoid pathological recursion on large/cyclic structures.
 */
export const _scrubSensitiveData = (value: unknown, depth = 0, seen = new WeakSet<object>()): unknown => {
  if (value === null || typeof value !== 'object') return value
  // Truncate (rather than pass through) anything past the depth bound, so a
  // sensitive key nested deeper than the limit can never escape unscrubbed.
  // Kept >= Sentry's `normalizeDepth` (10) so nothing Sentry serializes is missed.
  if (depth > 12) return '[TRUNCATED]'
  if (seen.has(value as object)) return value
  seen.add(value as object)

  if (Array.isArray(value)) return value.map(item => _scrubSensitiveData(item, depth + 1, seen))

  for (const key of Object.keys(value as Record<string, unknown>)) {
    const record = value as Record<string, unknown>
    const lowerKey = key.toLowerCase()
    if (SENSITIVE_KEYS.includes(lowerKey)) record[key] = '[REDACTED]'
    else if (URL_KEYS.includes(lowerKey) && typeof record[key] === 'string' && isUrlLike(record[key] as string)) record[key] = stripUrlQuery(record[key] as string)
    // Span/transaction `description` embeds the request URL ("GET https://…?token=…").
    else if (lowerKey === 'description' && typeof record[key] === 'string') record[key] = stripDescriptionUrl(record[key] as string)
    else record[key] = _scrubSensitiveData(record[key], depth + 1, seen)
  }

  return value
}

/**
 * Sentry `beforeSend` hook that strips sensitive data before transmission.
 *
 * Scrubs the request (headers/cookies/body/url), `extra`, `contexts`,
 * `breadcrumbs` and tracing `spans` — all paths where the Authorization header
 * or PII can surface (default HTTP breadcrumbs carry request URLs/bodies, the
 * HttpContext integration auto-fills `request.url` from `window.location`, and
 * BrowserTracing records per-request `http.url` on spans). URL-valued fields are
 * query-stripped by the scrubber itself (see `URL_KEYS`). Used for both
 * `beforeSend` (error events) and `beforeSendTransaction` (which carry `spans`).
 * Guarded so a scrub failure (e.g. mutating a frozen object) degrades to
 * dropping the unsafe sections rather than throwing — a throw here makes Sentry
 * silently discard the entire event. The failure is tagged so a broken scrubber
 * is detectable rather than masquerading as a context-free event.
 */
export const _scrubEvent = (event: Sentry.Event): Sentry.Event => {
  try {
    if (event.request) {
      event.request = _scrubSensitiveData(event.request) as NonNullable<Sentry.Event['request']>
      // `query_string` is a bare query (not a URL), so the scrubber's URL-key
      // stripping doesn't apply — drop it wholesale.
      delete event.request.query_string
    }
    if (event.extra) event.extra = _scrubSensitiveData(event.extra) as Record<string, unknown>
    if (event.contexts) event.contexts = _scrubSensitiveData(event.contexts) as NonNullable<Sentry.Event['contexts']>
    if (event.breadcrumbs) event.breadcrumbs = _scrubSensitiveData(event.breadcrumbs) as NonNullable<Sentry.Event['breadcrumbs']>
    if (event.spans) event.spans = _scrubSensitiveData(event.spans) as NonNullable<Sentry.Event['spans']>
  } catch {
    delete event.extra
    delete event.contexts
    delete event.breadcrumbs
    delete event.request
    delete event.spans
    event.tags = { ...event.tags, scrub_failed: 'true' }
  }

  return event
}

/**
 * Resolve the effective environment / release for Sentry.
 *
 * Runtime config (loaded from `/config.json`) wins over build-time env vars
 * because a single nginx image now serves multiple preview environments —
 * tagging Sentry events with the build-time SHA / environment would conflate
 * every preview into one bucket and defeat the runtime-config migration.
 *
 * Falls back to build-time values when runtime config is not yet loaded
 * (e.g. during the early bootstrap, before `loadRuntimeConfig()` resolves).
 */
const resolveSentryContext = (): { environment: string; release: string | undefined } => {
  try {
    const cfg = getRuntimeConfig()
    return {
      environment: cfg.environment || ENVIRONMENT,
      release: cfg.sentryRelease || BUILD_RELEASE,
    }
  } catch {
    return { environment: ENVIRONMENT, release: BUILD_RELEASE }
  }
}

/** Build the full Sentry config used by `initSentry`. */
const buildSentryConfig = (): Sentry.BrowserOptions => {
  const { environment, release } = resolveSentryContext()
  return {
    debug: false,
    dsn: SENTRY_DSN,
    environment,
    release,
    normalizeDepth: 10,
    maxBreadcrumbs: 10,
    beforeSend: _scrubEvent,
    beforeSendTransaction: _scrubEvent,
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes
        ),
      }),
      new ExtraErrorData({
        depth: 10
      }),
    ],

    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 0.01,
    ignoreErrors: [
      /Object Not Found Matching Id:\d+/gm,
      // 401 AxiosErrors are expected auth noise (expired/missing token, handled by
      // the auth flow). This backstop drops them on every capture path — unhandled
      // promise rejections, logger.error, direct captureException — mirroring the
      // API.logError guard from ISSUE-2093 (QA-259).
      /^Request failed with status code 401$/,
      // auth0-spa-js throws this from getTokenSilently() whenever there is no
      // session — the normal unauthenticated path, and expected on every socket
      // reconnect. 17,960 events across 8,534 users, none actionable (ISSUE-2943).
      /^Login required$/,
    ],
  }
}

/** Enables sentry user tracking */
export const trackSentryUser = (user: Auth0User) => {
  if (user) Sentry.setUser({
    id: user.sub,
    email: user.email,
  })
}

/** Disabled sentry user tracking */
export const untrackSentryUser = () => {
  Sentry.configureScope(scope => scope.setUser(null))
}

/** Initializes Sentry with full integrations and runtime-config-aware tags. */
export const initSentry = () => {
  if (isPreviewHost()) return false
  if (SENTRY_DSN) {
    Sentry.init(buildSentryConfig())
    return true
  }
  return false
}

/** React-router routes enhanced with sentry tracking */
export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)
